import React from "react";
import Paginator from "./Paginator";
import "./AppDataTable.scss";

/**
 * AppDataTable — themed table card with sticky header + sticky footer
 * (paginator). Only the body scrolls.
 *
 * Usage:
 *   <AppDataTable
 *     columns={[{ key: 'name', label: 'Name' }, ...]}
 *     pagination={{ page, totalPages, total, pageSize, onChange, infoLabel }}
 *     isEmpty={items.length === 0}
 *     emptyMessage="No records match your filters"
 *   >
 *     {items.map((item) => <YourRow key={item._id} item={item} />)}
 *   </AppDataTable>
 *
 * Renders <thead> from `columns` and uses children as <tbody> rows.
 * Columns: { key, label, width?, align?, className?, headClassName? }
 *
 * Place inside a flex-column page (e.g. `.app-page-fill`) so the table
 * fills the remaining height and the body scrolls between the sticky
 * header and footer.
 */
const AppDataTable = ({
  columns = [],
  pagination,
  isEmpty = false,
  emptyMessage = "No records found",
  className = "",
  tableClassName = "",
  children,
}) => {
  const colCount = columns.length || 1;

  return (
    <div className={`app-data-table ${className}`.trim()}>
      <div className="app-data-table__scroll">
        <table className={tableClassName}>
          {columns.length > 0 && (
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={col.headClassName || col.className || ""}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={colCount} className="app-data-table__empty">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              children
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="app-data-table__footer">
          <Paginator showSinglePage {...pagination} />
        </div>
      )}
    </div>
  );
};

export default AppDataTable;
